import {
  ShieldCheck,
  Wifi,
  Ticket as TicketIcon,
  Eye,
  Music,
  Headphones,
  Wrench,
} from 'lucide-react';

export type AgentKey = 'voice' | 'auth' | 'network' | 'ticket' | 'supervisor' | 'music' | 'mcp';

export interface AgentMeta {
  key: AgentKey;
  name: string;
  short: string;
  role: string;
  color: string;
  icon: React.ReactNode;
  /** Preferred speechSynthesis voice names (first match wins). */
  voiceHints: string[];
  /** Fallback pitch/rate so agents sound distinct even on one shared voice. */
  pitch: number;
  rate: number;
}

export const AGENTS: Record<AgentKey, AgentMeta> = {
  voice: {
    key: 'voice', name: 'Vokentra AI Agent', short: 'AI', role: 'Voice front-desk',
    color: '#e60000', icon: <Headphones className="w-full h-full p-1.5" />,
    voiceHints: ['Google UK English Female', 'Samantha', 'Microsoft Libby', 'Karen'], pitch: 1.0, rate: 1.0,
  },
  auth: {
    key: 'auth', name: 'AuthAgent', short: 'AU', role: 'Identity & fraud',
    color: '#3b82f6', icon: <ShieldCheck className="w-full h-full p-1.5" />,
    voiceHints: ['Google UK English Male', 'Daniel', 'Microsoft Ryan', 'Arthur'], pitch: 0.9, rate: 1.02,
  },
  network: {
    key: 'network', name: 'NetworkAgent', short: 'NE', role: 'Connectivity diagnostics',
    color: '#22c55e', icon: <Wifi className="w-full h-full p-1.5" />,
    voiceHints: ['Google US English', 'Alex', 'Microsoft Aria', 'Fred'], pitch: 1.05, rate: 1.05,
  },
  ticket: {
    key: 'ticket', name: 'TicketAgent', short: 'TK', role: 'Cases & billing',
    color: '#f59e0b', icon: <TicketIcon className="w-full h-full p-1.5" />,
    voiceHints: ['Tessa', 'Microsoft Sonia', 'Moira', 'Veena'], pitch: 1.1, rate: 1.0,
  },
  supervisor: {
    key: 'supervisor', name: 'SupervisorAgent', short: 'SV', role: 'Quality & escalation',
    color: '#a855f7', icon: <Eye className="w-full h-full p-1.5" />,
    voiceHints: ['Google UK English Male', 'Daniel', 'Rishi', 'Gordon'], pitch: 0.85, rate: 0.98,
  },
  music: {
    key: 'music', name: 'MusicAgent', short: 'MU', role: 'Hold music',
    color: '#ec4899', icon: <Music className="w-full h-full p-1.5" />,
    voiceHints: [], pitch: 1.0, rate: 1.0,
  },
  mcp: {
    key: 'mcp', name: 'MCP Tools', short: 'MCP', role: 'JSON-RPC tools / DB',
    color: '#64748b', icon: <Wrench className="w-full h-full p-1.5" />,
    voiceHints: [], pitch: 1.0, rate: 1.0,
  },
};

/** Map an orchestrator agent_type / trace key to an AgentKey. */
export function agentKeyFromType(t: string): AgentKey {
  const s = (t || '').toLowerCase();
  if (s.includes('auth')) return 'auth';
  if (s.includes('network')) return 'network';
  if (s.includes('ticket')) return 'ticket';
  if (s.includes('supervisor')) return 'supervisor';
  if (s.includes('music')) return 'music';
  if (s.includes('mcp') || s.includes('tool')) return 'mcp';
  return 'voice';
}

// ── Per-agent voices ────────────────────────────────────────────────────────
let cachedVoices: SpeechSynthesisVoice[] = [];

export function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return resolve([]);
    const existing = window.speechSynthesis.getVoices();
    if (existing.length) { cachedVoices = existing; return resolve(existing); }
    const handler = () => {
      cachedVoices = window.speechSynthesis.getVoices();
      resolve(cachedVoices);
    };
    window.speechSynthesis.onvoiceschanged = handler;
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 600);
  });
}

/** Pick a distinct SpeechSynthesisVoice for an agent, English preferred. */
export function voiceForAgent(key: AgentKey): SpeechSynthesisVoice | null {
  const voices = cachedVoices.length ? cachedVoices : (typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis.getVoices() : []);
  if (!voices.length) return null;
  const meta = AGENTS[key];
  for (const hint of meta.voiceHints) {
    const v = voices.find((x) => x.name.toLowerCase().includes(hint.toLowerCase()));
    if (v) return v;
  }
  // Fallback: spread English voices across agents by index for distinctness.
  const en = voices.filter((v) => v.lang?.toLowerCase().startsWith('en'));
  const pool = en.length ? en : voices;
  const order: AgentKey[] = ['voice', 'auth', 'network', 'ticket', 'supervisor'];
  const idx = Math.max(0, order.indexOf(key));
  return pool[idx % pool.length] || null;
}
